// src/components/analytics/NlpPartPredictorWidget.js
// Production-Ready NLP & Part Intelligence Widget with Real Data Integration

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Heading, Text, VStack, Spinner, Alert, AlertIcon, SimpleGrid,
  Stat, StatLabel, StatNumber, useColorModeValue, Flex,
  Button, useToast, HStack, Icon, IconButton, Tooltip, Badge, Progress,
  Collapse, useDisclosure, Menu, MenuButton, MenuList, MenuItem,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Input, InputGroup,
  InputLeftElement, Select, Tabs, TabList, TabPanels, Tab, TabPanel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Code, Divider, Tag, TagLabel, TagLeftIcon, Center,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, Skeleton
} from '@chakra-ui/react';
import { 
  MdModelTraining, MdInsights, MdRefresh, MdDownload, MdSearch,
  MdTrendingUp, MdHistory, MdInfo, MdCompareArrows,
  MdPublish, MdCheckCircle, MdWarning, MdTimeline, MdWaterDrop, 
  MdLocalFireDepartment, MdBolt, MdSpeed, MdVolumeUp, MdNotificationsActive,
  MdError
} from 'react-icons/md';
import { 
  ChevronDownIcon, ChevronUpIcon, CheckCircleIcon
} from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import aiService from '../../services/aiService';
import * as analyticsService from '../../services/analyticsService';
import Card from '../card/Cards';

// ============================================================================
// KEYFRAMES & ANIMATION VARIANTS
// ============================================================================

const livePulseKF = keyframes`
  0%   { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(52,211,153,0.7); }
  70%  { transform: scale(1);    box-shadow: 0 0 0 7px rgba(52,211,153,0); }
  100% { transform: scale(0.85); box-shadow: 0 0 0 0 rgba(52,211,153,0); }
`;

const FADE_UP = {
  hidden:  { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0,  transition: { duration: 0.26, ease: 'easeOut' } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.16 } },
};

const STAGGER_CONTAINER = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const LIST_ITEM_VARIANT = {
  hidden:  { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0,   transition: { duration: 0.22, ease: 'easeOut' } },
  exit:    { opacity: 0, x: 12,  transition: { duration: 0.14 } },
};

const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Map symptoms to icons
const getSymptomIcon = (symptom) => {
  const symptomLower = symptom?.toLowerCase() || '';
  switch(symptomLower) {
    case 'leak': return MdWaterDrop;
    case 'heat': 
    case 'overheat':
    case 'overheating': return MdLocalFireDepartment;
    case 'electrical': 
    case 'electric': return MdBolt;
    case 'vibration': 
    case 'vibrating': return MdSpeed;
    case 'noise': 
    case 'sound': return MdVolumeUp;
    case 'pressure': return MdWarning;
    default: return MdNotificationsActive;
  }
};

// Map symptoms to colors
const getSymptomColor = (symptom) => {
  const symptomLower = symptom?.toLowerCase() || '';
  switch(symptomLower) {
    case 'leak': return 'blue';
    case 'heat': 
    case 'overheat':
    case 'overheating': return 'red';
    case 'electrical': 
    case 'electric': return 'yellow';
    case 'vibration': 
    case 'vibrating': return 'orange';
    case 'noise': 
    case 'sound': return 'gray';
    case 'pressure': return 'purple';
    default: return 'gray';
  }
};

// ============================================================================
// SHARED UI ATOMS
// ============================================================================

/** Live pulsing dot — indicates active production model */
const LiveDot = React.memo(({ isLive = true }) => (
  <Box
    w="9px" h="9px" borderRadius="full" flexShrink={0}
    bg={isLive ? '#34d399' : 'gray.400'}
    style={{
      boxShadow:  isLive ? '0 0 8px #34d399' : 'none',
      animation:  isLive ? `${livePulseKF} 1.8s infinite` : 'none',
    }}
  />
));

/** Skeleton loader for the widget */
const NlpWidgetSkeleton = () => {
  const bd = useColorModeValue('gray.100', 'rgba(255,255,255,0.06)');
  return (
    <VStack spacing={5} align="stretch" p={2}>
      <Flex justify="space-between" align="center">
        <HStack spacing={4}>
          <Skeleton height="40px" w="40px" borderRadius="xl" />
          <VStack align="start" spacing={2}>
            <Skeleton height="16px" w="180px" />
            <Skeleton height="10px" w="100px" />
          </VStack>
        </HStack>
        <Skeleton height="32px" w="120px" borderRadius="md" />
      </Flex>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        {[...Array(4)].map((_, i) => (
          <Box key={i} p={4} borderWidth="1px" borderColor={bd} borderRadius="xl">
            <VStack spacing={2} align="flex-start">
              <Skeleton height="10px" w="70%" />
              <Skeleton height="24px" w="50%" />
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
      <Box borderWidth="1px" borderColor={bd} borderRadius="xl" p={4}>
        <Skeleton height="14px" w="200px" mb={4} />
        <Skeleton height="200px" borderRadius="lg" />
      </Box>
    </VStack>
  );
};

// ============================================================================
// ENHANCED BAR COMPONENT
// ============================================================================

const Bar = ({ label, value, maxValue, percentage, color = 'blue' }) => {
  const barColor = useColorModeValue(`${color}.500`, `${color}.300`);
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.800');
  const calculatedPercentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  return (
    <Box w="100%" p={2} _hover={{ bg: hoverBg }} borderRadius="md" transition="all 0.2s">
      <Flex justify="space-between" align="center" mb={1}>
        <Tooltip label={label} hasArrow placement="top">
          <Text fontSize="sm" fontWeight="medium" noOfLines={1} maxW="70%">{label}</Text>
        </Tooltip>
        <HStack spacing={2}>
          <Badge colorScheme={color}>{value}</Badge>
          {percentage !== undefined && <Text fontSize="xs" color="gray.500">{percentage.toFixed(1)}%</Text>}
        </HStack>
      </Flex>
      <Box w="100%" bg={bgColor} h="8px" borderRadius="full" overflow="hidden">
        <Box 
          w={`${calculatedPercentage}%`} 
          bg={barColor} 
          h="100%" 
          borderRadius="full"
          transition="width 0.5s ease-out"
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// MODEL COMPARISON MODAL
// ============================================================================

const ModelComparisonModal = ({ isOpen, onClose, models, activeModel, comparisonData, onPromote }) => {
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const statBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const activeRowBg = useColorModeValue('blue.50', 'blue.900');

  const getComparisonValue = (model, metric) => {
    if (!comparisonData || !comparisonData[model.model_version]) return 'N/A';
    const value = comparisonData[model.model_version][metric];
    return value !== null && value !== undefined ? value : 'N/A';
  };

  const getMetricColor = (value, isHigherBetter = true) => {
    if (value === 'N/A' || value === null || value === undefined) return 'gray';
    const numValue = parseFloat(value);
    if (isHigherBetter) {
      return numValue >= 90 ? 'green' : numValue >= 75 ? 'yellow' : 'red';
    }
    return numValue <= 50 ? 'green' : numValue <= 100 ? 'yellow' : 'red';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" bg="rgba(0,0,0,0.5)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader bg={headerBg}>
          <HStack>
            <Icon as={MdCompareArrows} boxSize={6} color="blue.500" />
            <Text fontWeight="800">Model Performance Comparison</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={6}>
          <VStack spacing={6} align="stretch">
            {activeModel && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">Current Production Model</Text>
                  <Text fontSize="sm">
                    {activeModel.model_name} v{activeModel.model_version} - 
                    Deployed: {new Date(activeModel.last_trained_at).toLocaleDateString()}
                  </Text>
                </Box>
              </Alert>
            )}

            <TableContainer borderWidth="1px" borderRadius="md" borderColor={borderColor}>
              <Table variant="simple" size="sm">
                <Thead bg={statBg}>
                  <Tr>
                    <Th>Model Name</Th>
                    <Th>Version</Th>
                    <Th>Status</Th>
                    <Th isNumeric>F1 Score</Th>
                    <Th isNumeric>Precision</Th>
                    <Th isNumeric>Recall</Th>
                    <Th isNumeric>Response Time</Th>
                    <Th>Trained Date</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {models.map((model) => {
                    const isActive = activeModel?.id === model.id;
                    const f1Score = getComparisonValue(model, 'f1_weighted');
                    const precision = getComparisonValue(model, 'precision');
                    const recall = getComparisonValue(model, 'recall');
                    const responseTime = getComparisonValue(model, 'avg_response_time');

                    return (
                      <Tr 
                        key={model.id} 
                        bg={isActive ? activeRowBg : 'transparent'}
                        fontWeight={isActive ? 'bold' : 'normal'}
                      >
                        <Td>
                          <HStack>
                            {isActive && <Icon as={MdCheckCircle} color="green.500" />}
                            <Text>{model.model_name}</Text>
                          </HStack>
                        </Td>
                        <Td><Badge colorScheme="purple">v{model.model_version}</Badge></Td>
                        <Td>
                          <Badge 
                            colorScheme={
                              model.status === 'production' ? 'green' : 
                              model.status === 'staging' ? 'blue' : 'gray'
                            }
                          >
                            {model.status}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme={getMetricColor(f1Score)}>
                            {f1Score !== 'N/A' ? `${parseFloat(f1Score).toFixed(2)}%` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme={getMetricColor(precision)}>
                            {precision !== 'N/A' ? `${parseFloat(precision).toFixed(2)}%` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme={getMetricColor(recall)}>
                            {recall !== 'N/A' ? `${parseFloat(recall).toFixed(2)}%` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme={getMetricColor(responseTime, false)}>
                            {responseTime !== 'N/A' ? `${responseTime}ms` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td>{new Date(model.last_trained_at).toLocaleDateString()}</Td>
                        <Td>
                          {!isActive && model.status === 'staging' && (
                            <Button 
                              size="xs" 
                              colorScheme="green" 
                              leftIcon={<MdPublish />} 
                              onClick={() => onPromote(model)}
                            >
                              Promote
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>

            {comparisonData && models.length > 0 && (
              <Box>
                <Heading size="sm" mb={4}>Performance Metrics Overview</Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  {models.slice(0, 3).map((model) => {
                    const metrics = comparisonData[model.model_version] || {};
                    const isActive = activeModel?.id === model.id;
                    
                    return (
                      <Box 
                        key={model.id} 
                        p={4} 
                        borderWidth="1px" 
                        borderRadius="md"
                        borderColor={isActive ? 'blue.400' : borderColor}
                        bg={statBg}
                      >
                        <VStack align="stretch" spacing={3}>
                          <HStack justify="space-between">
                            <Text fontWeight="bold" fontSize="sm">{model.model_name}</Text>
                            <Badge colorScheme={model.status === 'production' ? 'green' : 'blue'}>
                              {model.status}
                            </Badge>
                          </HStack>
                          
                          <Box>
                            <Flex justify="space-between" mb={1}>
                              <Text fontSize="xs">F1 Score</Text>
                              <Text fontSize="xs" fontWeight="bold">
                                {metrics.f1_weighted ? `${parseFloat(metrics.f1_weighted).toFixed(2)}%` : 'N/A'}
                              </Text>
                            </Flex>
                            <Progress 
                              value={metrics.f1_weighted || 0} 
                              colorScheme={getMetricColor(metrics.f1_weighted)}
                              size="sm"
                              borderRadius="full"
                            />
                          </Box>

                          <Box>
                            <Flex justify="space-between" mb={1}>
                              <Text fontSize="xs">Precision</Text>
                              <Text fontSize="xs" fontWeight="bold">
                                {metrics.precision ? `${parseFloat(metrics.precision).toFixed(2)}%` : 'N/A'}
                              </Text>
                            </Flex>
                            <Progress 
                              value={metrics.precision || 0} 
                              colorScheme={getMetricColor(metrics.precision)}
                              size="sm"
                              borderRadius="full"
                            />
                          </Box>

                          <Box>
                            <Flex justify="space-between" mb={1}>
                              <Text fontSize="xs">Recall</Text>
                              <Text fontSize="xs" fontWeight="bold">
                                {metrics.recall ? `${parseFloat(metrics.recall).toFixed(2)}%` : 'N/A'}
                              </Text>
                            </Flex>
                            <Progress 
                              value={metrics.recall || 0} 
                              colorScheme={getMetricColor(metrics.recall)}
                              size="sm"
                              borderRadius="full"
                            />
                          </Box>
                        </VStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ============================================================================
// MODEL PROMOTION DIALOG
// ============================================================================

const PromoteModelDialog = ({ isOpen, onClose, model, onConfirm, isLoading }) => {
  const cancelRef = React.useRef();
  const detailsBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered
    >
      <AlertDialogOverlay backdropFilter="blur(3px)" bg="rgba(0,0,0,0.45)">
        <AlertDialogContent borderRadius="xl">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            <HStack>
              <Icon as={MdPublish} color="green.500" />
              <Text>Promote Model to Production</Text>
            </HStack>
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">This action will:</Text>
                  <Text fontSize="sm">• Set the current production model to staging</Text>
                  <Text fontSize="sm">• Promote <strong>{model?.model_name} v{model?.model_version}</strong> to production</Text>
                  <Text fontSize="sm">• Route all prediction requests to the new model</Text>
                </Box>
              </Alert>

              {model && (
                <Box p={3} bg={detailsBg} borderRadius="md">
                  <Text fontWeight="bold" mb={2}>Model Details:</Text>
                  <SimpleGrid columns={2} spacing={2} fontSize="sm">
                    <Text color="gray.500">Version:</Text>
                    <Text fontWeight="medium">v{model.model_version}</Text>
                    <Text color="gray.500">Trained:</Text>
                    <Text fontWeight="medium">{new Date(model.last_trained_at).toLocaleDateString()}</Text>
                  </SimpleGrid>
                </Box>
              )}

              <Text fontSize="sm" color="gray.500">
                Are you sure you want to proceed with this promotion?
              </Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button 
              colorScheme="green" 
              onClick={onConfirm} 
              ml={3}
              isLoading={isLoading}
              loadingText="Promoting..."
              leftIcon={<MdPublish />}
            >
              Promote to Production
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

// ============================================================================
// FEEDBACK ANALYSIS ROW - FULLY RESPONSIVE & TRUNCATED
// ============================================================================

const FeedbackAnalysisRow = ({ analysis, borderColor, statBg, textColor }) => {
  const { isOpen, onToggle } = useDisclosure();
  const noteBg = useColorModeValue('gray.50', 'gray.900');
  
  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={statBg}
      overflow="hidden"
      position="relative"
      transition="all 0.2s"
      _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
    >
      <Flex
        p={4}
        cursor="pointer"
        onClick={onToggle}
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        gap={3}
      >
        {/* Left Side: Icon + Note Details */}
        <Flex flex="1" align="flex-start" gap={3} minW={0} w="full">
          <Icon as={MdInsights} color="blue.500" boxSize={5} flexShrink={0} mt={0.5} />
          <VStack align="flex-start" spacing={1} flex="1" minW={0}>
            <Flex wrap="wrap" gap={2} align="center">
              <Text fontWeight="bold" fontSize="sm" whiteSpace="nowrap">
                Work Order #{analysis.work_order_id}
              </Text>
              <Badge colorScheme="blue" fontSize="xs">
                {analysis.processed_date ? new Date(analysis.processed_date).toLocaleDateString() : 'N/A'}
              </Badge>
              {analysis.symptoms && (analysis.symptoms.includes('heat') || analysis.symptoms.includes('overheat')) && (
                <Badge colorScheme="red" variant="solid" fontSize="xs">
                  <Icon as={MdLocalFireDepartment} mr={1} boxSize={3} />
                  CRITICAL
                </Badge>
              )}
            </Flex>
            <Text fontSize="xs" color="gray.500" noOfLines={1} w="full">
              {analysis.raw_note}
            </Text>
          </VStack>
        </Flex>
        
        {/* Right Side: Badges & Chevron */}
        <Flex 
          wrap="wrap" 
          gap={2} 
          align="center" 
          justify={{ base: "flex-start", md: "flex-end" }} 
          w={{ base: "full", md: "auto" }}
          flexShrink={0}
        >
          <Badge colorScheme="purple">
            {analysis.extracted_parts?.length || 0} parts
          </Badge>
          {analysis.symptoms && analysis.symptoms.length > 0 && (
            <Badge colorScheme="orange">
              {analysis.symptoms.length} symptoms
            </Badge>
          )}
          <Icon 
            as={isOpen ? ChevronUpIcon : ChevronDownIcon} 
            boxSize={5}
            color="gray.500"
            display={{ base: 'none', md: 'block' }}
          />
        </Flex>
      </Flex>

      {/* Expanded Details */}
      <Collapse in={isOpen} animateOpacity>
        <Box p={4} pt={0} borderTopWidth="1px" borderColor={borderColor}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontWeight="bold" fontSize="sm" mb={2} color={textColor}>
                Original Technician Note:
              </Text>
              <Box p={3} bg={noteBg} borderRadius="md" borderLeftWidth="3px" borderLeftColor="blue.400">
                <Text fontSize="sm" fontStyle="italic">
                  "{analysis.raw_note}"
                </Text>
              </Box>
            </Box>

            {analysis.symptoms && analysis.symptoms.length > 0 && (
              <Box>
                <HStack mb={2}>
                  <Icon as={MdNotificationsActive} color="orange.500" boxSize={4} />
                  <Text fontWeight="bold" fontSize="sm" color={textColor}>
                    AI-Detected Symptoms ({analysis.symptoms.length}):
                  </Text>
                </HStack>
                <Flex gap={2} wrap="wrap">
                  {analysis.symptoms.map((symptom, idx) => (
                    <Tag key={idx} size="md" colorScheme={getSymptomColor(symptom)} borderRadius="full">
                      <TagLeftIcon boxSize="14px" as={getSymptomIcon(symptom)} />
                      <TagLabel>{symptom.toUpperCase()}</TagLabel>
                    </Tag>
                  ))}
                </Flex>
              </Box>
            )}

            {analysis.extracted_parts && analysis.extracted_parts.length > 0 && (
              <Box>
                <HStack mb={2}>
                  <Icon as={MdTrendingUp} color="purple.500" boxSize={4} />
                  <Text fontWeight="bold" fontSize="sm" color={textColor}>
                    AI-Identified Parts ({analysis.extracted_parts.length}):
                  </Text>
                </HStack>
                <Flex gap={2} wrap="wrap">
                  {analysis.extracted_parts.map((part, idx) => (
                    <Badge key={idx} colorScheme="purple" fontSize="xs" p={2} borderRadius="md">
                      <HStack spacing={1}>
                        <Text>{part.name || part.part_name}</Text>
                        {part.confidence && (
                          <Text fontSize="xs" opacity={0.7}>
                            ({(part.confidence * 100).toFixed(0)}%)
                          </Text>
                        )}
                      </HStack>
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            {(analysis.technician || analysis.location || analysis.issue_type) && (
              <Box>
                <Text fontWeight="bold" fontSize="sm" mb={2} color={textColor}>
                  Additional Context:
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} fontSize="xs">
                  {analysis.technician && (
                    <HStack>
                      <Text color="gray.500">Technician:</Text>
                      <Text fontWeight="medium">{analysis.technician}</Text>
                    </HStack>
                  )}
                  {analysis.location && (
                    <HStack>
                      <Text color="gray.500">Location:</Text>
                      <Text fontWeight="medium">{analysis.location}</Text>
                    </HStack>
                  )}
                  {analysis.issue_type && (
                    <HStack>
                      <Text color="gray.500">Issue Type:</Text>
                      <Badge colorScheme="orange" fontSize="xs">{analysis.issue_type}</Badge>
                    </HStack>
                  )}
                </SimpleGrid>
              </Box>
            )}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NlpPartPredictorWidget = ({ dateRange }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTraining, setIsTraining] = useState(false);
  const [isLoadingNlp, setIsLoadingNlp] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('mentions');
  const [processingStats, setProcessingStats] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  const [availableModels, setAvailableModels] = useState([]);
  const [activeModel, setActiveModel] = useState(null);
  const [selectedModelForPromotion, setSelectedModelForPromotion] = useState(null);
  const [isPromoting, setIsPromoting] = useState(false);
  const [comparisonData, setComparisonData] = useState({});
  const [modelMetrics, setModelMetrics] = useState(null);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  
  const [analyzedNotes, setAnalyzedNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [notesSearchTerm, setNotesSearchTerm] = useState('');
  const [notesSortBy, setNotesSortBy] = useState('date');

  const toast = useToast();
  const { isOpen: isComparisonOpen, onOpen: onComparisonOpen, onClose: onComparisonClose } = useDisclosure();
  const { isOpen: isPromoteDialogOpen, onOpen: onPromoteDialogOpen, onClose: onPromoteDialogClose } = useDisclosure();
  const { isOpen: isDetailsOpen, onToggle: onToggleDetails } = useDisclosure();

  const textColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const statBg = useColorModeValue("gray.50", "gray.700");
  
  const panelHeaderBg = useColorModeValue(
    'linear-gradient(135deg,rgba(49,130,206,0.04),rgba(56,178,172,0.04))',
    'linear-gradient(135deg,rgba(49,130,206,0.07),rgba(56,178,172,0.07))'
  );
  const headerIconBg = useColorModeValue('blue.50', 'rgba(49,130,206,0.1)');
  const headerIconBorder = useColorModeValue('blue.200', 'rgba(49,130,206,0.2)');
  const titleGradient = useColorModeValue('linear(to-r, blue.600, teal.500)', 'linear(to-r, #22d3ee, #34d399)');
  const iconButtonHoverBg = useColorModeValue('blue.50', 'rgba(49,130,206,0.1)');

  const scrollbarThumb = useColorModeValue('gray.300', 'gray.600');
  const scrollbarThumbHover = useColorModeValue('gray.400', 'gray.500');
  
  const scrollbarStyles = {
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: '4px' },
    '&::-webkit-scrollbar-thumb:hover': { background: scrollbarThumbHover }
  };

  const prodBadgeBg = useColorModeValue('green.50','rgba(52,211,153,0.08)');
  const prodBadgeBorder = useColorModeValue('green.200','rgba(52,211,153,0.2)');

  const fetchModels = useCallback(async () => {
    try {
      setIsLoadingModels(true);
      
      const response = await aiService.getModels({ modelType: 'PART_PREDICTION' });
      let models = response.models || [];
      
      if (models.length > 0 && !models.some(m => m.model_type === 'PART_PREDICTION')) {
          models = models.filter(m => m.model_type === 'PART_PREDICTION' || m.model_type === 'PART_PREDICTOR');
      }
      
      setAvailableModels(models);
      
      const prodModel = models.find(m => m.status === 'production');
      setActiveModel(prodModel);
      
      const metricsMap = {};
      for (const model of models) {
        try {
          const type = model.model_type || 'PART_PREDICTION';
          const metricsRes = await aiService.getModelMetrics(type, model.model_version);
          const metricsData = metricsRes.summary || metricsRes.data || metricsRes || {};
          
          metricsMap[model.model_version] = {
            f1_weighted: metricsData.f1_weighted || metricsData.f1_score || metricsData.f1Score,
            precision: metricsData.precision,
            recall: metricsData.recall,
            avg_response_time: metricsData.avg_response_time || metricsData.avgResponseTime
          };
        } catch (err) {
          console.warn(`Failed to fetch metrics for ${model.model_version}`, err);
          metricsMap[model.model_version] = {
            f1_weighted: null, precision: null, recall: null, avg_response_time: null
          };
        }
      }
      
      setComparisonData(metricsMap);
      
      if (prodModel && metricsMap[prodModel.model_version]) {
        setModelMetrics(metricsMap[prodModel.model_version]);
      }
    } catch (err) {
      console.error('[NLP-WIDGET] Error fetching models:', err);
      toast({ title: 'Failed to Load Models', status: 'error' });
    } finally {
      setIsLoadingModels(false);
    }
  }, [toast]);

  const fetchAnalyzedNotes = useCallback(async () => {
    try {
      setIsLoadingNotes(true);
      
      const response = await analyticsService.getAnalyzedNotes(dateRange);
      const notes = response.notes || [];
      
      setAnalyzedNotes(notes);
    } catch (err) {
      console.error('[NLP-WIDGET] Error fetching analyzed notes:', err);
      toast({
        title: 'Failed to Load Analyzed Notes',
        description: err.message,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      setAnalyzedNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  }, [dateRange, toast]);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await analyticsService.getNlpInsights(dateRange);

      setData(result);
      setLastRefresh(new Date());
      
      setProcessingStats({
        totalNotes: result?.totalNotesProcessed || 0,
        totalParts: result?.topMentionedParts?.length || 0,
        totalPhrases: result?.commonPhrases?.length || 0,
        processingTime: result?.processingTime || 'N/A'
      });
    } catch (err) {
      console.error('[NLP-WIDGET] Error:', err);
      setError(err.message || 'Failed to fetch NLP insights.');
      toast({
        title: 'Data Load Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, toast]);

  useEffect(() => {
    fetchData();
    fetchModels();
    fetchAnalyzedNotes();
  }, [fetchData, fetchModels, fetchAnalyzedNotes, refreshKey]);

  const handleRetrain = async () => {
    setIsTraining(true);
    toast({
      title: 'Model Retraining Started',
      description: 'Training the part predictor model. This may take several minutes.',
      status: 'info',
      duration: 5000,
      isClosable: true,
    });
    
    try {
      const response = await aiService.triggerPartPredictorTraining();
      toast({
        title: 'Training Completed',
        description: response.message || 'Model has been successfully retrained.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast({
        title: 'Training Failed',
        description: error.message || 'Could not complete the training job.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsTraining(false);
    }
  };

  const handleProcessNlp = async () => {
    setIsLoadingNlp(true);
    toast({
      title: 'Processing Feedback',
      description: 'Analyzing technician notes for insights and patterns.',
      status: 'info',
      duration: 5000,
      isClosable: true,
    });
    
    try {
      const response = await aiService.triggerNlpProcessing();
      toast({
        title: 'Processing Complete',
        description: response.message || 'Successfully processed new feedback.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      toast({
        title: 'Processing Failed',
        description: error.message || 'Could not process NLP feedback.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsLoadingNlp(false);
    }
  };

  const handlePromoteModel = async (model) => {
    setSelectedModelForPromotion(model);
    onPromoteDialogOpen();
  };

  const confirmPromoteModel = async () => {
    if (!selectedModelForPromotion) return;
    setIsPromoting(true);
    
    try {
      const type = selectedModelForPromotion.model_type || 'PART_PREDICTION';
      await aiService.promoteModel(type, selectedModelForPromotion.model_version);
      
      toast({
        title: 'Model Promoted Successfully',
        description: `${selectedModelForPromotion.model_name} v${selectedModelForPromotion.model_version} is now in production.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      
      await fetchModels();
      onPromoteDialogClose();
      onComparisonClose();
      setSelectedModelForPromotion(null);
    } catch (error) {
      toast({
        title: 'Promotion Failed',
        description: error.message || 'Could not promote model to production.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsPromoting(false);
    }
  };

  const handleOpenComparison = async () => {
    await fetchModels();
    onComparisonOpen();
  };

  const handleExport = useCallback(() => {
    if (!data) return;
    
    const exportData = {
      dateRange,
      exportDate: new Date().toISOString(),
      topMentionedParts: data.topMentionedParts,
      commonPhrases: data.commonPhrases,
      processingStats,
      modelMetrics,
      activeModel: activeModel ? {
        name: activeModel.model_name,
        version: activeModel.model_version,
        status: activeModel.status
      } : null,
      analyzedNotes
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nlp-insights-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Export Successful',
      description: 'Data has been exported to JSON file.',
      status: 'success',
      duration: 3000,
    });
  }, [data, dateRange, processingStats, modelMetrics, activeModel, analyzedNotes, toast]);

  const filteredAnalyzedNotes = useMemo(() => {
    if (!analyzedNotes || analyzedNotes.length === 0) return [];
    let notes = [...analyzedNotes];
    
    if (notesSearchTerm) {
      const searchLower = notesSearchTerm.toLowerCase();
      notes = notes.filter(note => 
        note.raw_note?.toLowerCase().includes(searchLower) ||
        note.work_order_id?.toLowerCase().includes(searchLower) ||
        note.technician?.toLowerCase().includes(searchLower) ||
        note.extracted_parts?.some(part => (part.name || part.part_name)?.toLowerCase().includes(searchLower)) ||
        note.symptoms?.some(symptom => symptom.toLowerCase().includes(searchLower))
      );
    }
    
    switch (notesSortBy) {
      case 'date':
        notes.sort((a, b) => new Date(b.processed_date) - new Date(a.processed_date));
        break;
      case 'workOrder':
        notes.sort((a, b) => (a.work_order_id || '').localeCompare(b.work_order_id || ''));
        break;
      case 'parts':
        notes.sort((a, b) => (b.extracted_parts?.length || 0) - (a.extracted_parts?.length || 0));
        break;
      default:
        break;
    }
    return notes;
  }, [analyzedNotes, notesSearchTerm, notesSortBy]);

  const filteredParts = useMemo(() => {
    if (!data?.topMentionedParts) return [];
    let parts = [...data.topMentionedParts];
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      parts = parts.filter(part => 
        part.part_name?.toLowerCase().includes(searchLower) ||
        part.item_id?.toLowerCase().includes(searchLower)
      );
    }
    
    switch (sortBy) {
      case 'mentions':
        parts.sort((a, b) => b.mention_count - a.mention_count);
        break;
      case 'name':
        parts.sort((a, b) => (a.part_name || '').localeCompare(b.part_name || ''));
        break;
      case 'trending':
        parts.sort((a, b) => (b.growth_rate || 0) - (a.growth_rate || 0));
        break;
      default:
        break;
    }
    return parts;
  }, [data, searchTerm, sortBy]);

  const maxMentionCount = filteredParts[0]?.mention_count || 0;
  const totalMentions = filteredParts.reduce((sum, part) => sum + part.mention_count, 0);

  if (isLoading && !data) {
    return (
      <Card>
        <NlpWidgetSkeleton />
      </Card>
    );
  }

  return (
    <Card>
      <Box px={0} pb={4} background={panelHeaderBg} borderRadius="xl" mb={6} p={4}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            <Box p={2.5} borderRadius="xl" bg={headerIconBg} borderWidth="1px" borderColor={headerIconBorder}>
              <Icon as={MdInsights} boxSize={5} color="blue.400" />
            </Box>
            <VStack spacing={0} align="flex-start">
              <Heading size="md" fontWeight="800" bgGradient={titleGradient} bgClip="text">
                NLP & Part Intelligence
              </Heading>
              <Text fontSize="9px" color={textColor} letterSpacing="0.1em">
                AI-DRIVEN FEEDBACK ANALYSIS
              </Text>
            </VStack>
            
            {activeModel && (
              <Tooltip label={`Active Model: ${activeModel.model_name} v${activeModel.model_version}`} hasArrow>
                <Flex align="center" gap={2} px={3} py={1.5} borderRadius="lg" bg={prodBadgeBg} borderWidth="1px" borderColor={prodBadgeBorder}>
                  <LiveDot isLive />
                  <Text fontSize="10px" fontWeight="700" color="green.500">
                    PRODUCTION · v{activeModel.model_version}
                  </Text>
                </Flex>
              </Tooltip>
            )}
          </HStack>
          
          <HStack spacing={2} flexWrap="wrap">
            <Tooltip label="Compare Models" hasArrow>
              <Button
                leftIcon={<MdCompareArrows />}
                size="sm"
                colorScheme="purple"
                variant="outline"
                onClick={handleOpenComparison}
                isLoading={isLoadingModels}
                borderRadius="lg"
              >
                Compare
              </Button>
            </Tooltip>
            <Tooltip label="Export Data" hasArrow>
              <IconButton
                icon={<MdDownload />}
                size="sm"
                variant="ghost"
                onClick={handleExport}
                isDisabled={!data}
                aria-label="Export data"
                borderRadius="lg"
                _hover={{ bg: iconButtonHoverBg }}
              />
            </Tooltip>
            <Tooltip label="Refresh Data" hasArrow>
              <IconButton
                icon={<MdRefresh />}
                size="sm"
                variant="ghost"
                onClick={() => setRefreshKey(prev => prev + 1)}
                isLoading={isLoading}
                aria-label="Refresh"
                borderRadius="lg"
                _hover={{ bg: iconButtonHoverBg }}
              />
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="bold">Error Loading Data</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
          <Button size="sm" onClick={() => setRefreshKey(prev => prev + 1)}>
            Retry
          </Button>
        </Alert>
      )}

      {modelMetrics && (
        <Box mb={6} p={4} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" mb={3}>
            <HStack>
              <Icon as={MdTimeline} color="purple.500" />
              <Text fontWeight="bold" fontSize="sm">Active Model Performance</Text>
            </HStack>
            {activeModel && (
              <Badge colorScheme="purple">v{activeModel.model_version}</Badge>
            )}
          </HStack>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Model Accuracy (F1)</Text>
              <HStack>
                <Text fontSize="2xl" fontWeight="bold">
                  {modelMetrics.f1_weighted ? `${parseFloat(modelMetrics.f1_weighted).toFixed(1)}%` : 'N/A'}
                </Text>
                {modelMetrics.f1_weighted && modelMetrics.f1_weighted >= 90 && (
                  <Icon as={CheckCircleIcon} color="green.500" />
                )}
              </HStack>
              {modelMetrics.f1_weighted && (
                <Progress 
                  value={modelMetrics.f1_weighted} 
                  colorScheme={modelMetrics.f1_weighted >= 90 ? 'green' : modelMetrics.f1_weighted >= 75 ? 'yellow' : 'red'}
                  size="xs"
                  mt={1}
                  borderRadius="full"
                />
              )}
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Precision</Text>
              <Text fontSize="2xl" fontWeight="bold">
                {modelMetrics.precision ? `${parseFloat(modelMetrics.precision).toFixed(1)}%` : 'N/A'}
              </Text>
              {modelMetrics.precision && (
                <Progress 
                  value={modelMetrics.precision} 
                  colorScheme="blue"
                  size="xs"
                  mt={1}
                  borderRadius="full"
                />
              )}
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Recall</Text>
              <Text fontSize="2xl" fontWeight="bold">
                {modelMetrics.recall ? `${parseFloat(modelMetrics.recall).toFixed(1)}%` : 'N/A'}
              </Text>
              {modelMetrics.recall && (
                <Progress 
                  value={modelMetrics.recall} 
                  colorScheme="teal"
                  size="xs"
                  mt={1}
                  borderRadius="full"
                />
              )}
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Avg Response Time</Text>
              <Text fontSize="2xl" fontWeight="bold">
                {modelMetrics.avg_response_time ? `${modelMetrics.avg_response_time}ms` : 'N/A'}
              </Text>
              {modelMetrics.avg_response_time && (
                <Badge 
                  colorScheme={modelMetrics.avg_response_time < 50 ? 'green' : modelMetrics.avg_response_time < 100 ? 'yellow' : 'red'}
                  mt={1}
                >
                  {modelMetrics.avg_response_time < 50 ? 'Excellent' : modelMetrics.avg_response_time < 100 ? 'Good' : 'Needs Optimization'}
                </Badge>
              )}
            </Box>
          </SimpleGrid>
        </Box>
      )}

      {processingStats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Notes Processed</StatLabel>
            <StatNumber fontSize="2xl">{processingStats.totalNotes}</StatNumber>
          </Stat>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Unique Parts</StatLabel>
            <StatNumber fontSize="2xl">{processingStats.totalParts}</StatNumber>
          </Stat>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Common Phrases</StatLabel>
            <StatNumber fontSize="2xl">{processingStats.totalPhrases}</StatNumber>
          </Stat>
          <Stat bg={statBg} p={3} borderRadius="md">
            <StatLabel fontSize="xs">Processing Time</StatLabel>
            <StatNumber fontSize="lg">{processingStats.processingTime}</StatNumber>
          </Stat>
        </SimpleGrid>
      )}

      {data && !error && (
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab><Icon as={MdTrendingUp} mr={2} /> Top Parts</Tab>
            <Tab><Icon as={MdInsights} mr={2} /> Common Phrases</Tab>
            <Tab>
              <Icon as={MdInfo} mr={2} /> 
              Analyzed Notes
              {analyzedNotes.length > 0 && (
                <Badge ml={2} colorScheme="blue">{analyzedNotes.length}</Badge>
              )}
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <Flex gap={3} wrap="wrap">
                  <InputGroup maxW="300px">
                    <InputLeftElement pointerEvents="none">
                      <MdSearch color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search parts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="sm"
                    />
                  </InputGroup>
                  
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    size="sm"
                    maxW="200px"
                  >
                    <option value="mentions">Most Mentions</option>
                    <option value="name">Alphabetical</option>
                    <option value="trending">Trending</option>
                  </Select>
                  
                  <Badge alignSelf="center" colorScheme="purple">
                    {filteredParts.length} parts • {totalMentions} total mentions
                  </Badge>
                </Flex>

                {filteredParts.length > 0 ? (
                  <Box maxH="450px" overflowY="auto" pr={2} css={scrollbarStyles}>
                    <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
                      <VStack align="stretch" spacing={2}>
                        {filteredParts.map((part, idx) => (
                          <motion.div key={part.item_id || idx} variants={LIST_ITEM_VARIANT}>
                            <Bar 
                              label={`${idx + 1}. ${part.part_name}`}
                              value={part.mention_count}
                              maxValue={maxMentionCount}
                              percentage={(part.mention_count / totalMentions) * 100}
                              color={idx < 3 ? 'blue' : 'gray'}
                            />
                          </motion.div>
                        ))}
                      </VStack>
                    </motion.div>
                  </Box>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    No parts found matching your search criteria.
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack align="stretch" spacing={3}>
                {data.commonPhrases && data.commonPhrases.length > 0 ? (
                  <Box maxH="450px" overflowY="auto" pr={2} css={scrollbarStyles}>
                    <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
                      <VStack align="stretch" spacing={3}>
                        {data.commonPhrases.map((phrase, index) => (
                          <motion.div key={index} variants={LIST_ITEM_VARIANT}>
                            <Box
                              p={4}
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                              bg={statBg}
                              _hover={{ borderColor: 'blue.400', shadow: 'sm' }}
                              transition="all 0.2s"
                            >
                              <Flex justify="space-between" align="center">
                                <Box flex="1">
                                  <Text fontWeight="medium" fontSize="sm" color={textColor}>
                                    "{phrase.phrase}"
                                  </Text>
                                  <HStack mt={1} spacing={2}>
                                    <Badge colorScheme="green">{phrase.count} occurrences</Badge>
                                    {phrase.sentiment && (
                                      <Badge colorScheme={phrase.sentiment === 'positive' ? 'green' : phrase.sentiment === 'negative' ? 'red' : 'gray'}>
                                        {phrase.sentiment}
                                      </Badge>
                                    )}
                                  </HStack>
                                </Box>
                                <Progress
                                  value={(phrase.count / data.commonPhrases[0].count) * 100}
                                  size="sm"
                                  colorScheme="blue"
                                  w="100px"
                                  borderRadius="full"
                                />
                              </Flex>
                            </Box>
                          </motion.div>
                        ))}
                      </VStack>
                    </motion.div>
                  </Box>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    No common phrases found in the selected date range.
                  </Alert>
                )}
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack align="stretch" spacing={4}>
                <Flex gap={3} wrap="wrap" align="center">
                  <InputGroup maxW="400px">
                    <InputLeftElement pointerEvents="none">
                      <MdSearch color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search notes, parts, symptoms, work orders..."
                      value={notesSearchTerm}
                      onChange={(e) => setNotesSearchTerm(e.target.value)}
                      size="sm"
                    />
                  </InputGroup>
                  
                  <Select
                    value={notesSortBy}
                    onChange={(e) => setNotesSortBy(e.target.value)}
                    size="sm"
                    maxW="200px"
                  >
                    <option value="date">Most Recent</option>
                    <option value="workOrder">Work Order</option>
                    <option value="parts">Most Parts Found</option>
                  </Select>
                  
                  <Badge alignSelf="center" colorScheme="blue">
                    {filteredAnalyzedNotes.length} analyzed notes
                  </Badge>

                  {isLoadingNotes && (
                    <Spinner size="sm" color="blue.500" />
                  )}
                </Flex>

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">AI Analysis Insights</Text>
                    <Text fontSize="xs">
                      View raw technician notes alongside AI-extracted parts, symptoms, and key insights. 
                      Click each card to see detailed analysis.
                    </Text>
                  </Box>
                </Alert>

                {filteredAnalyzedNotes.length > 0 ? (
                  <Box maxH="450px" overflowY="auto" pr={2} css={scrollbarStyles}>
                    <motion.div variants={STAGGER_CONTAINER} initial="hidden" animate="visible">
                      <VStack align="stretch" spacing={3}>
                        {filteredAnalyzedNotes.map((analysis) => (
                          <motion.div key={analysis.id} variants={LIST_ITEM_VARIANT}>
                            <FeedbackAnalysisRow
                              analysis={analysis}
                              borderColor={borderColor}
                              statBg={statBg}
                              textColor={textColor}
                            />
                          </motion.div>
                        ))}
                      </VStack>
                    </motion.div>
                  </Box>
                ) : (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text>
                      {isLoadingNotes 
                        ? 'Loading analyzed notes...' 
                        : notesSearchTerm 
                          ? 'No notes found matching your search criteria.' 
                          : 'No analyzed notes available for the selected date range.'}
                    </Text>
                  </Alert>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      <Divider my={6} />

      <VStack align="stretch" spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <VStack align="flex-start" spacing={2}>
            <Button
              colorScheme="blue"
              leftIcon={<MdModelTraining />}
              onClick={handleRetrain}
              isLoading={isTraining}
              loadingText="Training..."
              isDisabled={isLoading || isLoadingNlp}
              size="md"
              w="full"
              borderRadius="xl"
            >
              Retrain Part Predictor
            </Button>
            <Text fontSize="xs" color="gray.500">
              Retrain the AI model to improve part prediction accuracy based on latest data.
            </Text>
          </VStack>

          <VStack align="flex-start" spacing={2}>
            <Button
              colorScheme="green"
              leftIcon={<MdInsights />}
              onClick={handleProcessNlp}
              isLoading={isLoadingNlp}
              loadingText="Processing..."
              isDisabled={isLoading || isTraining}
              size="md"
              w="full"
              borderRadius="xl"
            >
              Process New Feedback
            </Button>
            <Text fontSize="xs" color="gray.500">
              Extract insights from new technician notes to update analytics widgets.
            </Text>
          </VStack>
        </SimpleGrid>

        <Box p={4} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" mb={3}>
            <HStack>
              <Icon as={MdPublish} color="green.500" />
              <Text fontWeight="bold" fontSize="sm">Model Management</Text>
            </HStack>
            <Badge colorScheme={availableModels.filter(m => m.status === 'staging').length > 0 ? 'orange' : 'gray'}>
              {availableModels.filter(m => m.status === 'staging').length} staging models
            </Badge>
          </HStack>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <VStack align="stretch" spacing={2}>
              <Button
                leftIcon={<MdCompareArrows />}
                onClick={handleOpenComparison}
                isLoading={isLoadingModels}
                size="sm"
                variant="outline"
                colorScheme="purple"
                w="full"
                borderRadius="lg"
              >
                Compare Model Performance
              </Button>
              <Text fontSize="xs" color="gray.500">
                View detailed comparison of all available models and their metrics.
              </Text>
            </VStack>

            <VStack align="stretch" spacing={2}>
              {availableModels.some(m => m.status === 'staging') ? (
                <>
                  <Menu>
                    <MenuButton 
                      as={Button} 
                      rightIcon={<ChevronDownIcon />}
                      leftIcon={<MdPublish />}
                      colorScheme="green"
                      size="sm"
                      w="full"
                      borderRadius="lg"
                      isDisabled={!availableModels.some(m => m.status === 'staging')}
                    >
                      Promote Model
                    </MenuButton>
                    <MenuList>
                      {availableModels
                        .filter(m => m.status === 'staging')
                        .map(model => (
                          <MenuItem 
                            key={model.id}
                            onClick={() => handlePromoteModel(model)}
                          >
                            <VStack align="flex-start" spacing={0}>
                              <Text fontWeight="medium">{model.model_name} v{model.model_version}</Text>
                              <Text fontSize="xs" color="gray.500">
                                F1: {comparisonData?.[model.model_version]?.f1_weighted?.toFixed(1) || 'N/A'}%
                              </Text>
                            </VStack>
                          </MenuItem>
                        ))}
                    </MenuList>
                  </Menu>
                  <Text fontSize="xs" color="gray.500">
                    Promote a staging model to production after reviewing performance.
                  </Text>
                </>
              ) : (
                <>
                  <Button
                    leftIcon={<MdPublish />}
                    colorScheme="green"
                    size="sm"
                    w="full"
                    borderRadius="lg"
                    isDisabled
                  >
                    No Models to Promote
                  </Button>
                  <Text fontSize="xs" color="gray.500">
                    Train a new model to create a staging candidate for promotion.
                  </Text>
                </>
              )}
            </VStack>
          </SimpleGrid>
        </Box>

        <Box>
          <Button
            size="sm"
            variant="ghost"
            rightIcon={isDetailsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            onClick={onToggleDetails}
          >
            Advanced Details
          </Button>
          <Collapse in={isDetailsOpen} animateOpacity>
            <Box mt={3} p={4} bg={statBg} borderRadius="md" fontSize="sm">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Model Information</Text>
                  <VStack align="flex-start" spacing={1}>
                    <Code fontSize="xs">Version: {activeModel?.model_version || 'N/A'}</Code>
                    <Code fontSize="xs">Algorithm: TF-IDF + Random Forest</Code>
                    <Code fontSize="xs">Deployed: {activeModel ? new Date(activeModel.last_trained_at).toLocaleDateString() : 'N/A'}</Code>
                  </VStack>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Processing Pipeline</Text>
                  <VStack align="flex-start" spacing={1}>
                    <Code fontSize="xs">1. Tokenization & Cleaning</Code>
                    <Code fontSize="xs">2. Symptom Detection (NLP)</Code>
                    <Code fontSize="xs">3. Part Extraction (Entity Recognition)</Code>
                    <Code fontSize="xs">4. Classification (Random Forest)</Code>
                  </VStack>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Performance Thresholds</Text>
                  <VStack align="flex-start" spacing={1}>
                    <HStack>
                      <Badge colorScheme="green">Excellent</Badge>
                      <Text fontSize="xs">F1 ≥ 90%</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="yellow">Good</Badge>
                      <Text fontSize="xs">75% ≤ F1 &lt; 90%</Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme="red">Needs Improvement</Badge>
                      <Text fontSize="xs">F1 &lt; 75%</Text>
                    </HStack>
                  </VStack>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>Available Models</Text>
                  <VStack align="flex-start" spacing={1}>
                    {availableModels.map(model => (
                      <HStack key={model.id} spacing={2}>
                        <Badge colorScheme={
                          model.status === 'production' ? 'green' : 
                          model.status === 'staging' ? 'blue' : 'orange'
                        }>
                          {model.status}
                        </Badge>
                        <Text fontSize="xs">v{model.model_version}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </SimpleGrid>
            </Box>
          </Collapse>
        </Box>
      </VStack>

      <ModelComparisonModal 
        isOpen={isComparisonOpen} 
        onClose={onComparisonClose} 
        models={availableModels} 
        activeModel={activeModel} 
        comparisonData={comparisonData} 
        onPromote={handlePromoteModel} 
      />

      <PromoteModelDialog
        isOpen={isPromoteDialogOpen}
        onClose={onPromoteDialogClose}
        model={selectedModelForPromotion}
        onConfirm={confirmPromoteModel}
        isLoading={isPromoting}
      />
    </Card>
  );
};

export default NlpPartPredictorWidget;